import { useEffect, useState } from "react";

/**
 * Google Ads tracking parameters captured from the landing URL and
 * persisted to localStorage so they survive client-side navigation
 * (e.g. a visitor clicking through to another page before submitting a form).
 *
 * Used by the -google ad landing pages to tag every lead as coming from
 * Google Ads and to pass gclid / gbraid / wbraid / utm_* into the CRM.
 */

export interface GoogleAdsParams {
  leadSource: string;
  landingPage: string;
  gclid: string;
  gbraid: string;
  wbraid: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
}

const STORAGE_KEY = "google_ads_params";
export const GOOGLE_ADS_CONTACT_TAG = "Google Ads - SAINT";

const readParamsFromUrl = (): Partial<GoogleAdsParams> => {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const out: Partial<GoogleAdsParams> = {};
  const gclid = p.get("gclid");
  const gbraid = p.get("gbraid");
  const wbraid = p.get("wbraid");
  const utmSource = p.get("utm_source");
  const utmMedium = p.get("utm_medium");
  const utmCampaign = p.get("utm_campaign");
  const utmTerm = p.get("utm_term");
  const utmContent = p.get("utm_content");
  if (gclid) out.gclid = gclid;
  if (gbraid) out.gbraid = gbraid;
  if (wbraid) out.wbraid = wbraid;
  if (utmSource) out.utmSource = utmSource;
  if (utmMedium) out.utmMedium = utmMedium;
  if (utmCampaign) out.utmCampaign = utmCampaign;
  if (utmTerm) out.utmTerm = utmTerm;
  if (utmContent) out.utmContent = utmContent;
  return out;
};

const loadStored = (): Partial<GoogleAdsParams> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return {};
};

const saveStored = (params: Partial<GoogleAdsParams>) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(params));
  } catch {
    /* ignore */
  }
};

/**
 * On first mount, merge any URL params into the stored params (URL wins),
 * persist the merged set, then return the merged params plus the current
 * page path as landingPage and "Google Ads" as leadSource.
 */
export const useGoogleAdsParams = (): GoogleAdsParams => {
  const [params, setParams] = useState<GoogleAdsParams>({
    leadSource: "Google Ads",
    landingPage: typeof window !== "undefined" ? window.location.pathname : "",
    gclid: "",
    gbraid: "",
    wbraid: "",
    utmSource: "",
    utmMedium: "",
    utmCampaign: "",
    utmTerm: "",
    utmContent: "",
  });

  useEffect(() => {
    const urlParams = readParamsFromUrl();
    const stored = loadStored();
    const merged = { ...stored, ...urlParams };
    // Always pin landingPage to the first -google page the visitor landed on
    if (!merged.landingPage) {
      merged.landingPage = window.location.pathname;
    }
    saveStored(merged);
    setParams({
      leadSource: "Google Ads",
      landingPage: merged.landingPage,
      gclid: merged.gclid || "",
      gbraid: merged.gbraid || "",
      wbraid: merged.wbraid || "",
      utmSource: merged.utmSource || "",
      utmMedium: merged.utmMedium || "",
      utmCampaign: merged.utmCampaign || "",
      utmTerm: merged.utmTerm || "",
      utmContent: merged.utmContent || "",
    });
  }, []);

  return params;
};

// Custom field IDs for Google Ads tracking params (registered in the CRM)
export const GOOGLE_ADS_FIELD_IDS = {
  leadSource: "LtidcVDfap85rzeaaI0g",
  landingPage: "K0lBoXOmUHNMAdILvqzB",
  gclid: "xLPOfzDTYv5dV4jeQu7C",
  gbraid: "dqxtwt0zWz4rb4evjYSJ",
  wbraid: "EZbcuEj1Y075E2sECVKt",
  utmSource: "FFtLFRPfp4CpP6XHHUqc",
  utmMedium: "asEOCOA78F2QIBZT6VOu",
  utmCampaign: "RJpY7MrDyM0JCLUYz6BL",
  utmTerm: "lad1vka4j1owBXUernQN",
  utmContent: "WsbPgbeBcihqMiBvyyFQ",
  contactTag: "pup3YYSIgc4JrtRdr6LJ",
} as const;
